/**
 * Signals (ADR 0016) — mirror of `core/signals.py`; both pass `tests/vectors/signals.json`.
 *
 * - `effective(values, definitions, nowMs)`: expired values and switched-off signals are left out (unknown).
 * - `signalNode(ast, signals)`: one `{signal, op: value}` node → true, false or null (unknown).
 * - `fit(task, evaluate, rules, prefer)`: +1 per condition that holds, -1 per one that does not, 0 when
 *   unknown; `hidden` only when the task asks for it (`props.whenHide`) and its own condition is false.
 */
import type { Task } from "./schema";

export interface SignalValue {
  value: unknown;
  at: string;
  expiresAt?: string | null;
  note?: string | null;
  source?: string | null;
}
export type Signals = Record<string, SignalValue>;
export type SignalAst = Record<string, unknown>;

export interface SignalRule {
  filter?: SignalAst | null;
  when: SignalAst;
}

export interface FitReason {
  source: "task" | "rule" | "bin";
  when: SignalAst;
  holds: boolean | null;
  signals: string[];
}

export function effective(
  values: Signals,
  definitions: Record<string, { enabled?: boolean } | undefined> | null | undefined,
  nowMs: number,
): Signals {
  const out: Signals = {};
  for (const [name, rec] of Object.entries(values ?? {})) {
    if (!rec || typeof rec !== "object" || definitions?.[name]?.enabled === false) continue;
    if (typeof rec.expiresAt === "string" && rec.expiresAt && Date.parse(rec.expiresAt) <= nowMs) continue;
    out[name] = rec;
  }
  return out;
}

const isNum = (v: unknown): v is number => typeof v === "number";

export function signalNode(ast: SignalAst, signals: Signals | null | undefined): boolean | null {
  const rec = signals?.[String(ast.signal)];
  if (!rec) return null;
  const v = rec.value;
  if ("eq" in ast) return v === ast.eq;
  if ("neq" in ast) return v !== ast.neq;
  if ("gt" in ast) return isNum(v) && isNum(ast.gt) && v > ast.gt;
  if ("lt" in ast) return isNum(v) && isNum(ast.lt) && v < ast.lt;
  if ("gte" in ast) return isNum(v) && isNum(ast.gte) && v >= ast.gte;
  if ("lte" in ast) return isNum(v) && isNum(ast.lte) && v <= ast.lte;
  if ("in" in ast && Array.isArray(ast.in)) {
    const list = ast.in as unknown[];
    return Array.isArray(v) ? v.some((x) => list.includes(x)) : list.includes(v);
  }
  if ("has" in ast) return Array.isArray(v) ? v.includes(ast.has) : v === ast.has;
  return v !== null && v !== undefined && v !== false;
}

/** The signals an AST refers to (for "why?"). */
export function namesIn(ast: unknown): string[] {
  if (!ast || typeof ast !== "object") return [];
  const a = ast as SignalAst;
  if ("signal" in a) return [String(a.signal)];
  const out: string[] = [];
  for (const key of ["and", "or"])
    for (const x of (a[key] as unknown[] | undefined) ?? []) out.push(...namesIn(x));
  if ("not" in a) out.push(...namesIn(a.not));
  return out;
}

export function fit(
  task: Task,
  evaluate: (task: Task, ast: SignalAst | null | undefined) => boolean | null,
  rules?: SignalRule[] | null,
  prefer?: SignalAst | null,
): { score: number; hidden: boolean; reasons: FitReason[] } {
  let score = 0;
  const reasons: FitReason[] = [];
  let own: boolean | null = null;
  const add = (source: FitReason["source"], when: SignalAst) => {
    const r = evaluate(task, when);
    score += r === true ? 1 : r === false ? -1 : 0;
    reasons.push({ source, when, holds: r, signals: namesIn(when) });
    return r;
  };
  const when = task.props.when;
  if (when && typeof when === "object" && Object.keys(when).length) own = add("task", when as SignalAst);
  for (const rule of rules ?? []) {
    if (rule && rule.when && Object.keys(rule.when).length && evaluate(task, rule.filter ?? null) !== false)
      add("rule", rule.when);
  }
  if (prefer && Object.keys(prefer).length) add("bin", prefer);
  return { score, hidden: task.props.whenHide === true && own === false, reasons };
}
